import { useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Skeleton,
  SimpleGrid,
  Modal,
  Select,
  Divider,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconFileText,
  IconDownload,
  IconPrinter,
  IconPlus,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import {
  useAccountStatement,
  useDownloadStatementPdf,
  useGenerateStatement,
  useStatementHistory,
  useDownloadGeneratedStatementPdf,
} from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

export default function Statement() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    dayjs().subtract(30, 'day').toDate(),
    new Date(),
  ]);

  const startDate = dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : '';
  const endDate = dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : '';

  const { data: statement, isLoading } = useAccountStatement({
    start_date: startDate,
    end_date: endDate,
  });
  const downloadPdf = useDownloadStatementPdf();
  const generateStatement = useGenerateStatement();
  const { data: history } = useStatementHistory();
  const downloadGenerated = useDownloadGeneratedStatementPdf();

  const [generateOpen, setGenerateOpen] = useState(false);
  const [genType, setGenType] = useState<string | null>('monthly');
  const [genDateRange, setGenDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const handleGenerate = () => {
    if (!genType) return;
    const payload: { statement_type: string; start_date?: string; end_date?: string } = {
      statement_type: genType,
    };
    if (genType === 'custom') {
      if (!genDateRange[0] || !genDateRange[1]) return;
      payload.start_date = dayjs(genDateRange[0]).format('YYYY-MM-DD');
      payload.end_date = dayjs(genDateRange[1]).format('YYYY-MM-DD');
    }
    generateStatement.mutate(payload, {
      onSuccess: () => {
        notifications.show({ title: 'Statement Generated', message: 'Your statement has been generated successfully.', color: 'green' });
        setGenerateOpen(false);
      },
      onError: () => {
        notifications.show({ title: 'Error', message: 'Failed to generate statement. Please try again.', color: 'red' });
      },
    });
  };

  const handleDownloadGenerated = (statementId: number) => {
    downloadGenerated.mutate(statementId, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `statement-${statementId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => {
        notifications.show({ title: 'Download failed', message: 'Unable to download statement PDF.', color: 'red' });
      },
    });
  };

  const handleDownloadPdf = () => {
    if (!startDate || !endDate) return;
    downloadPdf.mutate(
      { start_date: startDate, end_date: endDate },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `statement-${startDate}-to-${endDate}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
        },
        onError: () => {
          notifications.show({
            title: 'Download failed',
            message: 'Unable to download statement. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Account Statement"
        description="View your account transactions and running balance"
        actions={
          <Group gap="sm">
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setGenerateOpen(true)}
            >
              Generate Statement
            </Button>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadPdf}
              loading={downloadPdf.isPending}
              disabled={!startDate || !endDate}
            >
              Download PDF
            </Button>
            <Button
              variant="subtle"
              leftSection={<IconPrinter size={16} />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Group>
        }
      />

      <Paper>
        <Group>
          <DatePickerInput
            type="range"
            label="Date Range"
            placeholder="Select date range"
            value={dateRange}
            onChange={setDateRange}
            maxDate={new Date()}
            size="sm"
            w={300}
          />
        </Group>
      </Paper>

      {!startDate || !endDate ? (
        <EmptyState
          icon={<IconFileText size={30} />}
          title="Select a date range"
          description="Choose start and end dates to view your account statement"
        />
      ) : isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : !statement ? (
        <EmptyState
          icon={<IconFileText size={30} />}
          title="No data available"
          description="No transactions found for the selected period"
        />
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
            <StatCard
              title="Opening Balance"
              value={`$${parseFloat(statement.opening_balance).toFixed(2)}`}
              icon={<IconFileText size={24} />}
            />
            <StatCard
              title="Total Invoiced"
              value={`$${parseFloat(statement.total_invoiced).toFixed(2)}`}
              icon={<IconFileText size={24} />}
            />
            <StatCard
              title="Total Paid"
              value={`$${parseFloat(statement.total_paid).toFixed(2)}`}
              icon={<IconFileText size={24} />}
            />
            <StatCard
              title="Closing Balance"
              value={`$${parseFloat(statement.closing_balance).toFixed(2)}`}
              icon={<IconFileText size={24} />}
            />
          </SimpleGrid>

          <Paper>
            <Text fw={600} mb="sm">
              {statement.customer_name} ({statement.customer_code})
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              {dayjs(startDate).format('MMM D, YYYY')} — {dayjs(endDate).format('MMM D, YYYY')}
            </Text>

            {statement.transactions.length > 0 ? (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Reference</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Debit</Table.Th>
                    <Table.Th ta="right">Credit</Table.Th>
                    <Table.Th ta="right">Balance</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {statement.transactions.map((txn) => (
                    <Table.Tr key={txn.id}>
                      <Table.Td>
                        <Text size="sm">{dayjs(txn.date).format('MMM D, YYYY')}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" tt="capitalize" fw={500}>{txn.type}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{txn.reference}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{txn.description}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {parseFloat(txn.debit) > 0 && (
                          <Text size="sm" c="red">${parseFloat(txn.debit).toFixed(2)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {parseFloat(txn.credit) > 0 && (
                          <Text size="sm" c="brand">${parseFloat(txn.credit).toFixed(2)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" fw={500}>${parseFloat(txn.balance).toFixed(2)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="lg">
                No transactions for this period.
              </Text>
            )}
          </Paper>
        </>
      )}

      <Divider my="lg" />

      <Text fw={700} size="lg" mb="sm">Statement History</Text>
      {history?.items && history.items.length > 0 ? (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Start Date</Table.Th>
                <Table.Th>End Date</Table.Th>
                <Table.Th>Generated At</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {history.items.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td><Text size="sm" tt="capitalize">{item.statement_type}</Text></Table.Td>
                  <Table.Td><Text size="sm">{dayjs(item.start_date).format('MMM D, YYYY')}</Text></Table.Td>
                  <Table.Td><Text size="sm">{dayjs(item.end_date).format('MMM D, YYYY')}</Text></Table.Td>
                  <Table.Td><Text size="sm">{dayjs(item.generated_at).format('MMM D, YYYY h:mm A')}</Text></Table.Td>
                  <Table.Td>
                    <Tooltip label="Download PDF">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => handleDownloadGenerated(item.id)}
                        loading={downloadGenerated.isPending}
                      >
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <Text size="sm" c="dimmed">No generated statements yet.</Text>
      )}

      <Modal opened={generateOpen} onClose={() => setGenerateOpen(false)} title="Generate Statement" size="md">
        <Stack gap="md">
          <Select
            label="Statement Type"
            data={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'custom', label: 'Custom' },
            ]}
            value={genType}
            onChange={setGenType}
            required
          />
          {genType === 'custom' && (
            <DatePickerInput
              type="range"
              label="Custom Date Range"
              placeholder="Select date range"
              value={genDateRange}
              onChange={setGenDateRange}
              maxDate={new Date()}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setGenerateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              loading={generateStatement.isPending}
              disabled={!genType || (genType === 'custom' && (!genDateRange[0] || !genDateRange[1]))}
            >
              Generate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
